<template>
    <b-modal
        id="add-slu-module-modal"
        ref="add-slu-module-modal"
        size="lg"
        :title="$t('slu_dashboard.add_modules')"
        modal-class="apprentice-default-modal add-slu-module"
        :ok-title="$t('common.save')"
        cancel-variant="outline-primary"
        :cancel-title="$t('common.cancel')"
        @show="load"
        @ok="onOk">
        <div class="add-slu-module">
            <div
                class="slu-module-banner-preview"
                v-if="banner_preview !== null">
                <b-img :src="banner_preview"></b-img>
            </div>

            <div
                class="errors"
                v-if="hasErrors">
                <b-alert
                    v-for="(error, index) in errors"
                    v-if="error.error"
                    show
                    variant="danger"
                    :key="`error-${index}`"
                    >{{ error.message }}
                </b-alert>
            </div>

            <div class="name-and-banner-inputs">
                <b-form-group
                    class="slu-module-name"
                    label-for="slu-module-name-input"
                    :label="$t('common.name')">
                    <template v-slot:label> {{ $t('common.name') }} <span class="required">*</span></template>
                    <b-form-input
                        id="slu-module-name-input"
                        v-model="name"
                        :placeholder="$t('common.name')">
                    </b-form-input>
                </b-form-group>
                <b-form-group
                    class="slu-module-banner"
                    :label="$t('slu_dashboard.image')"
                    label-for="slu-module-banner-input">
                    <template v-slot:label> {{ $t('slu_dashboard.image') }}<span class="required">*</span></template>
                    <b-form-file
                        class="apprentice-file-upload"
                        id="slu-module-banner-input"
                        v-model="file"
                        accept="image/jpeg,image/png"
                        :placeholder="$t('slu_dashboard.upload_banner')"
                        @change="showPreview"></b-form-file>
                </b-form-group>
            </div>

            <div class="description">
                <b-form-group
                    class="slu-module-description"
                    label-for="slu-module-description-rte"
                    :label="$t('common.description')">
                    <template v-slot:label> {{ $t('common.description') }} <span class="required">*</span> </template>
                    <ckeditor
                        id="slu-module-description-rte"
                        v-model="description"
                        :editor="editor"
                        :config="editorConfig">
                    </ckeditor>
                </b-form-group>
            </div>

            <b-form-group
                class="slu-module-hours"
                label-for="slu-module-hours-input"
                :label="$t('slu_dashboard.hours')">
                <b-form-input
                    id="slu-module-hours-input"
                    v-model="hours"
                    type="number"
                    min="0"
                    max="150"
                    :placeholder="$t('slu_dashboard.amount_of_hours')">
                </b-form-input>
            </b-form-group>
            <b-form-group
                class="slu-module-label"
                label-for="slu-module-labels"
                label="labels">
                <design-2021-apprentice-multiselect-component
                    v-model="labels"
                    label="name"
                    :options="sluLabelsIn"
                    track-by="id">
                </design-2021-apprentice-multiselect-component>
            </b-form-group>
            <b-form-group
                class="slu-module-required-group"
                label-for="slu-module-required-input"
                :label="$t('slu_dashboard.mandatory_module')">
                <b-form-checkbox
                    id="slu-module-required-input"
                    v-model="required"
                    switch>
                </b-form-checkbox>
            </b-form-group>

            <div
                class="stdc-sync-status"
                v-if="sluSchoolSettingsIn.stc_sync">
                <b-form-group
                    class="sync-with-std-group"
                    label-for="sync-with std-button"
                    :label="$t('slu_dashboard.connected_with_somtoday')">
                    <b-badge
                        class="badge-std-sync"
                        :variant="stcCode !== null ? 'success' : 'danger'">
                        {{ stcCode !== null ? 'Ja' : 'Nee' }}
                    </b-badge>
                </b-form-group>

                <b-button
                    class="btn-sync-stdc"
                    variant="primary"
                    @click="openSomTodayConnectModal">
                    <i class="btn-chain-link"></i>{{ $t('slu_dashboard.connect_to_somtoday') }}
                </b-button>

                <design-2021-slu-som-today-connect-modal-component
                    ref="select-stc-uuid"
                    :slu-cohort-in="sluCohortIn"
                    @updateStcCode="updateStcCode($event)">
                </design-2021-slu-som-today-connect-modal-component>
            </div>
        </div>
    </b-modal>
</template>

<script>
import ApprenticeEditor from '../../../helpers/ApprenticeEditor';

export default {
    name: 'design-2021-add-slu-module-modal-component',
    props: ['sluCohortIn', 'sluLabelsIn', 'sluSchoolSettingsIn'],
    data() {
        return {
            name: null,
            hours: null,
            description: '',
            file: null,
            stcCode: null,
            banner_preview: null,
            errorsFound: false,
            labels: [],
            errors: {
                name: {
                    error: false,
                    message: this.$t('slu_dashboard.name_cannot_be_empty'),
                },
                description: {
                    error: false,
                    message: this.$t('slu_dashboard.description_cannot_be_empty'),
                },
                banner: {
                    error: false,
                    message: this.$t('slu_dashboard.you_have_to_upload_a_banner'),
                },
            },
            editor: ApprenticeEditor,
            editorConfig: {
                placeholder: this.$t('slu_dashboard.module_description'),
                bodyClass: 'slu-module-body',
                toolbar: {
                    items: [
                        'heading',
                        '|',
                        'bold',
                        'italic',
                        'underline',
                        '|',
                        'link',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'outdent',
                        'indent',
                        'alignment',
                        '|',
                        'imageInsert',
                        'mediaEmbed',
                        'insertTable',
                        '|',
                        'undo',
                        'redo',
                    ],
                },
                language: this.$authUser.locale,
                image: {
                    toolbar: [
                        'imageTextAlternative',
                        '|',
                        'imageStyle:alignLeft',
                        'imageStyle:alignRight',
                        'imageStyle:alignCenter',
                        'imageStyle:inline',
                        '|',
                        'linkImage',
                    ],
                },
                table: {
                    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
                },
                simpleUpload: {
                    uploadUrl: this.routes.baseUrl() + '/image/upload',
                },
            },
            sluCohort: this.sluCohortIn,
            required: true,
        };
    },
    methods: {
        onOk(bvModal) {
            if (this.hasErrors()) {
                bvModal.preventDefault();
                return;
            }

            const labelIds = this.labels.map((label) => label.id);
            const hours = this.hours === null ? 0 : this.hours;
            const required = this.required ? 1 : 0;
            const formData = new FormData();
            formData.append('name', this.name);
            formData.append('description', this.description);
            formData.append('hours', hours);
            formData.append('required', required);
            formData.append('file', this.file);
            for (let i = 0; i < labelIds.length; i++) {
                formData.append('labels[' + i + ']', labelIds[i]);
            }
            if (this.sluSchoolSettingsIn.stc_sync && this.stcCode !== null) {
                formData.append('stcCode', this.stcCode.code);
            }
            const headers = { 'Content-Type': 'multipart/form-data' };
            this.alert.saving();
            axios
                .post(this.routes.sluModule(this.sluCohortIn.slu_course_id, this.sluCohortIn.id), formData, {
                    headers: headers,
                })
                .then(
                    (response) => {
                        this.$emit('updateModules', response.data);
                        this.alert.success();
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
        },

        load() {
            this.name = null;
            this.hours = null;
            this.description = '';
            this.file = null;
            this.required = true;
            this.banner_preview = null;
        },

        showPreview(evt) {
            const file = evt.target.files[0];
            this.banner_preview = URL.createObjectURL(file);
        },

        hasErrors() {
            this.errorsFound = false;
            this.errors.name.error = false;
            this.errors.description.error = false;
            this.errors.banner.error = false;
            let errors = false;

            if (this.name === null || this.name === '') {
                this.errorsFound = true;
                this.errors.name.error = true;
                errors = true;
            }

            if (this.description === null || this.description === '') {
                this.errorsFound = true;
                this.errors.description.error = true;
                errors = true;
            }

            if (this.file === null || this.file === '') {
                this.errorsFound = true;
                this.errors.banner.error = true;
                errors = true;
            }

            return errors;
        },
        openSomTodayConnectModal() {
            this.$refs['select-stc-uuid'].show();
        },
        updateStcCode(code) {
            this.stcCode = code;
        },
    },
};
</script>

<style scoped lang="scss">
.slu-module-banner {
    height: 73px;
}

.slu-module-banner-preview {
    margin-bottom: $grid-gutter-width;

    img {
        object-fit: cover;
        object-position: center;
        width: 100%;
        height: 150px;
    }
}

.add-slu-module {
    input {
        margin-bottom: 0;
    }
}

.slu-module-required-group ::v-deep {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    column-gap: map-get($grid-gutter-spacing, 'fifteen');

    label {
        margin-bottom: 0;
    }
}

.sync-with-std-group,
.btn-sync-stdc {
    display: flex;
    align-items: center;
    justify-content: flex-start;
}

.sync-with-std-group ::v-deep {
    column-gap: map-get($grid-gutter-spacing, 'fifteen');

    label {
        margin-bottom: 0;
    }

    .badge-std-sync {
        color: $white;
        font-size: $base-font-size;
        font-weight: $font-weight-normal;
        width: 100%;
    }
}

.btn-sync-stdc {
    column-gap: map-get($grid-gutter-spacing, 'eight');
    margin-top: map-get($grid-gutter-spacing, 'fifteen');
    width: 100%;

    i {
        font-style: normal;
        color: $white;
    }
}

.stdc-sync-status {
    margin-top: map-get($grid-gutter-spacing, 'fifteen');
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .btn-sync-stdc {
        width: auto;
    }
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .name-and-banner-inputs,
    .hours-and-mandatory {
        display: flex;
        flex-direction: row;
        column-gap: $grid-gutter-width;
        justify-content: flex-start;
        align-items: flex-start;
    }

    .slu-module-name,
    .slu-module-banner {
        flex: 1 0 calc(50% - 15px);
    }

    .slu-module-hours {
        flex: 1 0 10%;
    }
}
</style>
